'use client';

import type { CheckCdcPrerequisitesResponse, ParameterCheckResult } from '@repo/api-gw-sdk';
import { CdcBackupModeCheckResultModeEnum, PrivateLinkCheckResultStatusEnum, SqlSetupCheckStatus } from '@repo/api-gw-sdk';
import { useCallback } from 'react';
import { useDAL } from '@/contexts/useDAL';
const POLL_INTERVAL_MS = 5000;
// A SQL verification finishes in seconds, unlike PrivateLink provisioning which takes minutes, so it
// warrants a tighter poll for the hub's step status.
const SQL_CHECK_POLL_INTERVAL_MS = 2000;

/**
 * Parameters that carry a verdict. An unmeasured one is displayed but never counted for or against
 * the step: how large it needs to be follows from the running server, which the prerequisites check
 * cannot see on its own.
 */
export const gatingParameterChecks = (checks: ParameterCheckResult[]) => checks.filter(param => !param.unmeasured);

/**
 * Derived from a response rather than from hook state, so a caller that just awaited `refresh()` can
 * judge the freshly-fetched checks instead of the render-stale ones.
 */
export const deriveCdcSteps = (checks: CheckCdcPrerequisitesResponse | undefined) => {
  const privateLinkStatus = checks?.privateLinkCheck?.status;
  const parameterChecks = checks?.parameterChecks ?? [];
  const isPrivateLinkStepDone = !!checks?.ramShareCheck?.found && privateLinkStatus === PrivateLinkCheckResultStatusEnum.Active;
  // An unmeasured parameter blocks the step rather than being skipped: "we could not judge this"
  // must not read as "this is fine", or CDC gets enabled against limits nobody checked.
  const gatingChecks = gatingParameterChecks(parameterChecks);
  const isParametersStepDone = gatingChecks.length === parameterChecks.length && gatingChecks.length > 0 && gatingChecks.every(param => param.ok);
  const isSqlStepDone = checks?.sqlSetupCheck?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_OK;
  return {
    isPrivateLinkStepDone,
    isParametersStepDone,
    isSqlStepDone,
    allStepsDone: isPrivateLinkStepDone && isParametersStepDone && isSqlStepDone
  };
};

// The setup workflow writes the PrivateLink record asynchronously, so right after a start the server
// still reports not_setup. During this grace window that reading is treated as provisioning: the hub
// keeps polling instead of going quiet on "Not configured", and a second click must not start another
// workflow — setupAwsPrivateLink is not idempotent.
const PRIVATE_LINK_SETUP_GRACE_MS = 90_000;
// Module scope: the marker must outlive the dialog that starts the setup and be visible to every
// consumer of the hook.
const privateLinkSetupStartedAt = new Map<string, number>();
export const markPrivateLinkSetupStarted = (resourceId: string) => {
  privateLinkSetupStartedAt.set(resourceId, Date.now());
};
export const isPrivateLinkSetupGraceActive = (resourceId: string) => {
  const startedAt = privateLinkSetupStartedAt.get(resourceId);
  return startedAt !== undefined && Date.now() - startedAt < PRIVATE_LINK_SETUP_GRACE_MS;
};

// A cmd Lambda stays warm for minutes after an invocation, so re-opening the modal in that window
// would pay for a warm-up that changes nothing.
const VERIFIER_WARM_GRACE_MS = 5 * 60_000;
// Module scope: the marker has to outlive the dialog that fired the warm-up.
const verifierWarmedAt = new Map<string, number>();

/** Reports whether a warm-up is due for the resource, and records it when it is. */
export const shouldWarmVerifier = (resourceId: string) => {
  const warmedAt = verifierWarmedAt.get(resourceId);
  if (warmedAt !== undefined && Date.now() - warmedAt < VERIFIER_WARM_GRACE_MS) {
    return false;
  }
  verifierWarmedAt.set(resourceId, Date.now());
  return true;
};
interface RdsCdcStatus {
  isLoading: boolean;
  /** Re-fetches the heavy prerequisites key and resolves with the fresh response. */
  refresh: () => Promise<CheckCdcPrerequisitesResponse | undefined>;
  mode: CdcBackupModeCheckResultModeEnum;
  isEligible: boolean;
  ineligibleReason: string | undefined;
  isPrivateLinkActive: boolean;
  isPrivateLinkProvisioning: boolean;
  isPrivateLinkTearingDown: boolean;
  parameterChecks: ParameterCheckResult[];
  isPrivateLinkStepDone: boolean;
  isParametersStepDone: boolean;
  isSqlStepDone: boolean;
  allStepsDone: boolean;
}

/**
 * Single source of the CDC status derivations shared by the Backup details row, the onboarding hub,
 * and its sub-modals. They all read the same SWR key, so `refresh` in a sub-modal updates the hub and
 * the row too.
 */
export const useRdsCdcStatus = (resourceId: string): RdsCdcStatus => {
  const dal = useDAL();
  const {
    data: checks,
    isLoading,
    mutate: refreshPrerequisites
  } = dal.inventory.privateLink.useCheckCdcPrerequisites(resourceId, {
    // The heavy key polls only for PrivateLink settling: it runs live AWS scans that take seconds
    // per call, so a running SQL verification is followed through the cheap verdict key below.
    refreshInterval: data => {
      const privateLinkStatus = data?.privateLinkCheck?.status;
      const isPrivateLinkSettling = privateLinkStatus === PrivateLinkCheckResultStatusEnum.InProgress || privateLinkStatus === PrivateLinkCheckResultStatusEnum.TearingDown || isPrivateLinkSetupGraceActive(resourceId) && privateLinkStatus !== PrivateLinkCheckResultStatusEnum.Active;
      return isPrivateLinkSettling ? POLL_INTERVAL_MS : 0;
    }
  });

  // The verdict is served from the cheap cached read, which answers in milliseconds, so it can be
  // polled tightly while a verification runs — keeping the hub's step live even when the SQL dialog
  // is closed — without re-running the heavy AWS scans.
  const {
    data: verdictData,
    mutate: refreshVerdict
  } = dal.inventory.privateLink.useCdcSqlSetupCheck(resourceId, {
    refreshInterval: latest => latest?.sqlSetupCheck?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS ? SQL_CHECK_POLL_INTERVAL_MS : 0
  });

  // Both keys together: the verdict below overrides the heavy response's copy, so refreshing only
  // the heavy key would leave a stale cached verdict masking the value it just fetched. useCallback
  // keeps the identity stable — consumers list refresh as an effect dependency, and a per-render
  // closure would refire their effects (and this AWS scan) on every render.
  const refresh = useCallback(async () => {
    const [prerequisites] = await Promise.all([refreshPrerequisites(), refreshVerdict()]);
    return prerequisites;
  }, [refreshPrerequisites, refreshVerdict]);
  const privateLinkStatus_0 = checks?.privateLinkCheck?.status;
  const isPrivateLinkActive = privateLinkStatus_0 === PrivateLinkCheckResultStatusEnum.Active;
  const isPrivateLinkProvisioning = privateLinkStatus_0 === PrivateLinkCheckResultStatusEnum.InProgress || isPrivateLinkSetupGraceActive(resourceId) && !isPrivateLinkActive;
  const isPrivateLinkTearingDown = privateLinkStatus_0 === PrivateLinkCheckResultStatusEnum.TearingDown;

  // The cheap key refreshes during and right after a verification while the heavy key does not, so
  // its verdict wins; the heavy response is the fallback for consumers mounted before the first
  // cheap read resolves.
  const sqlSetupCheck = verdictData?.sqlSetupCheck ?? checks?.sqlSetupCheck;
  const steps = deriveCdcSteps(checks);
  const isSqlStepDone = sqlSetupCheck?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_OK;
  return {
    isLoading,
    refresh,
    mode: checks?.cdcBackupModeCheck?.mode ?? CdcBackupModeCheckResultModeEnum.None,
    isEligible: checks?.eligible ?? false,
    ineligibleReason: checks?.ineligibleReason,
    isPrivateLinkActive,
    isPrivateLinkProvisioning,
    isPrivateLinkTearingDown,
    parameterChecks: checks?.parameterChecks ?? [],
    isPrivateLinkStepDone: steps.isPrivateLinkStepDone,
    isParametersStepDone: steps.isParametersStepDone,
    isSqlStepDone,
    allStepsDone: steps.isPrivateLinkStepDone && steps.isParametersStepDone && isSqlStepDone
  };
};